import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';

export const LiveUpcomingMatchesSection = ({ onWatch }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveScores, setLiveScores] = useState({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const loadMatches = async () => {
      try {
        // Get live games and all scheduled games
        const [liveRes, allGamesRes] = await Promise.all([
          api.getLiveGames(),
          api.getGames({ limit: 20 })
        ]);
        
        // Get live games
        const liveGames = liveRes.data.map(g => ({ ...g, isLive: true }));
        
        // Get upcoming/scheduled games sorted by time
        const scheduledGames = allGamesRes.data
          .filter(g => g.status === 'scheduled')
          .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
          .slice(0, 6)
          .map(g => ({ ...g, isLive: false }));
        
        // Combine: live first, then upcoming
        const combined = [...liveGames, ...scheduledGames].slice(0, 8);
        
        setMatches(combined);
      } catch (error) {
        console.error('Error loading live/upcoming matches:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMatches();
  }, []);

  // Fetch live scores for matches with feed URLs
  useEffect(() => {
    const fetchLiveScores = async () => {
      const matchesWithFeed = matches.filter(m => m.score_feed_url);
      
      for (const match of matchesWithFeed) {
        try {
          const res = await api.getGameLiveScore(match.id);
          if (res.data.text) {
            setLiveScores(prev => ({ ...prev, [match.id]: res.data.text }));
          }
        } catch (error) {
          console.error(`Error fetching live score for ${match.id}:`, error);
        }
      }
    };

    if (matches.length > 0) {
      fetchLiveScores();
      // Refresh every 30 seconds
      const interval = setInterval(fetchLiveScores, 30000);
      return () => clearInterval(interval);
    }
  }, [matches]);

  // Auto-scroll carousel effect
  useEffect(() => {
    if (matches.length <= 3 || !scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cardWidth = 304; // 288px card + 16px gap
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    const interval = setInterval(() => {
      setScrollPosition(prev => {
        const newPos = prev + cardWidth;
        if (newPos >= maxScroll) {
          return 0; // Reset to start
        }
        return newPos;
      });
    }, 4000); // Rotate every 4 seconds
    
    return () => clearInterval(interval);
  }, [matches]);

  // Apply scroll position
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [scrollPosition]);

  const formatMatchTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMs < 0) return 'Started';
    if (diffHours < 1) return 'Starting soon';
    if (diffHours < 24) return `In ${diffHours}h`;
    if (diffDays === 1) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="h-6 bg-gray-700 rounded w-56 mb-4 animate-pulse" />
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-72 h-32 bg-gray-800 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <section className="py-8 px-4" data-testid="live-upcoming-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Live & Upcoming Matches
          </h2>
          <Link to="/fixtures" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div 
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
          onMouseEnter={() => setScrollPosition(scrollContainerRef.current?.scrollLeft || 0)}
        >
          {matches.map(match => {
            // Generate short team names (last word, first 3 chars)
            const homeShort = match.home_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'HOM';
            const awayShort = match.away_team_name?.split(' ').slice(-1)[0]?.substring(0, 3).toUpperCase() || 'AWY';
            
            return (
              <div
                key={match.id}
                onClick={() => onWatch(match)}
                className="flex-shrink-0 w-72 bg-gray-800/60 rounded-xl p-4 cursor-pointer hover:bg-gray-800/80 transition-colors border border-gray-700/50 hover:border-sky-500/50"
              >
                <div className="flex items-center justify-between mb-3">
                  {match.isLive || match.status === 'live' ? (
                    <Badge className="bg-red-500 text-white text-xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                      LIVE
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs">
                      {formatMatchTime(match.scheduled_time)}
                    </Badge>
                  )}
                  <span className="text-gray-400 text-xs truncate ml-2">{match.league_name}</span>
                </div>
                
                {/* Vertical Team Layout */}
                <div className="flex flex-col items-center gap-2">
                  {/* Home Team */}
                  <div className="flex items-center gap-2 w-full justify-center">
                    {match.home_team_logo ? (
                      <img src={resolveMediaUrl(match.home_team_logo)} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center text-xs font-bold text-white">
                        {match.home_team_name?.charAt(0)}
                      </div>
                    )}
                    <span className="text-white font-medium text-sm">{match.home_team_name}</span>
                  </div>
                  
                  {/* Score or VS */}
                  <div className="text-center">
                    {(match.isLive || match.status === 'live' || match.status === 'finished') && match.home_score !== null ? (
                      <div className="text-white font-bold text-lg">
                        {match.home_score} - {match.away_score}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">vs.</span>
                    )}
                  </div>
                  
                  {/* Away Team */}
                  <div className="flex items-center gap-2 w-full justify-center">
                    {match.away_team_logo ? (
                      <img src={resolveMediaUrl(match.away_team_logo)} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center text-xs font-bold text-white">
                        {match.away_team_name?.charAt(0)}
                      </div>
                    )}
                    <span className="text-white font-medium text-sm">{match.away_team_name}</span>
                  </div>
                </div>
                
                {/* Live Score Feed Text with Team Abbreviations */}
                {liveScores[match.id] && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-white font-bold text-xs bg-gray-700 px-1.5 py-0.5 rounded">{homeShort}</span>
                      <span className="text-sky-400 text-sm font-medium">{liveScores[match.id]}</span>
                      <span className="text-white font-bold text-xs bg-gray-700 px-1.5 py-0.5 rounded">{awayShort}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
